import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { View, Text, ScrollView, TouchableOpacity, ActivityIndicator, Image, StyleSheet, Platform, Linking, Alert, Modal } from 'react-native';
import { useRoute, useNavigation } from '@react-navigation/native';
import { globalStyles } from '../styles/globalStyles';
import { venueListStyles } from '../styles/venueListStyles';
import { homeStyles } from '../styles/HomeStyles';
import { getResponsiveValue } from '../styles/breakpoints';
import { SearchBar } from '../components/SearchBar';
import { Ionicons } from '@expo/vector-icons';
import { MediaViewer } from '../components/MediaViewer';
import { useCommunityModal } from '../contexts/CommunityModalContext';
import { useUser } from '../components/LoginPersistence';
import { useCommunity } from '../contexts/CommunityContext';
import { CustomModal } from '../components/Modal';
import { CommunityAuthForm } from '../components/CommunityAuthForm';
import { CommunityMemberUpgradeForm } from '../components/CommunityMemberUpgradeForm';

const getFileType = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();

  // Image types
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension)) {
    return 'image';
  }

  // PDF
  if (extension === 'pdf') {
    return 'pdf';
  }

  // Audio types
  if (['mp3', 'wav', 'aac', 'flac', 'ogg', 'm4a', 'wma'].includes(extension)) {
    return 'audio';
  }

  // Video types
  if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension)) {
    return 'video';
  }

  // Default to file
  return 'file';
};

const CommunityList = forwardRef(({}, ref) => {
  const route = useRoute();
  const navigation = useNavigation();
  const { user: currentUserData } = useUser();
  const { openMessaging } = useCommunityModal();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCommunityMemberSuccessModal, setShowCommunityMemberSuccessModal] = useState(false);
  const { 
    entities, 
    allEntities, 
    loading: communityLoading, 
    hasMore, 
    refreshEntities, 
    loadMoreEntities, 
    filterEntitiesBySearch 
  } = useCommunity();

  const [displayedEntities, setDisplayedEntities] = useState([]);
  const [allFilteredEntities, setAllFilteredEntities] = useState([]); // Store all filtered results
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEnhancedSearch, setIsEnhancedSearch] = useState(false);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(null);
  const [isMediaViewerVisible, setIsMediaViewerVisible] = useState(false);
  const [currentUserMedia, setCurrentUserMedia] = useState(null);
  const [legendVisible, setLegendVisible] = useState(false);
  const [gateModalVisible, setGateModalVisible] = useState(false);
  const [gateModalTitle, setGateModalTitle] = useState('');
  const [gateModalMessage, setGateModalMessage] = useState('');
  const [accessBlocked, setAccessBlocked] = useState(false);
  const lastScrollY = useRef(0);

  // Handle media press to open MediaViewer
  const handleMediaPress = (entity, mediaIndex) => {
    const isOrganization = entity.entityType === 'organization';
    const mediaItems = isOrganization 
      ? (entity.media || [])
      : (entity.publicMedia?.media || []);
    
    if (mediaItems && mediaItems.length > 0) {
      setCurrentUserMedia(mediaItems);
      setSelectedMediaIndex(mediaIndex);
      setIsMediaViewerVisible(true);
    }
  };

  const handleMediaViewerClose = () => {
    setIsMediaViewerVisible(false);
    setSelectedMediaIndex(null);
    setCurrentUserMedia(null);
  };

  // Sync displayed entities with context entities when not searching
  useEffect(() => {
    if (!searchQuery) {
      setDisplayedEntities(entities);
      setAllFilteredEntities([]);
    }
  }, [entities, searchQuery]);

  // Trigger search when allEntities becomes available and there's a search query
  useEffect(() => {
    if (searchQuery && allEntities.length > 0) {
      performSearch(searchQuery);
    }
  }, [allEntities]);

  // Get search parameters from navigation
  useEffect(() => {
    if (route.params?.searchQuery) {
      setSearchQuery(route.params.searchQuery);
      // Wait for allEntities to be loaded before searching
      if (allEntities.length > 0) {
        performSearch(route.params.searchQuery);
      }
    } else {
      setSearchQuery(''); // Clear search query
      setAllFilteredEntities([]); // Reset all filtered entities
      setDisplayedEntities(entities);
    }
  }, [route.params, allEntities]);

  // Gate: Admin-only access for now
  // useEffect(() => {
  //   const checkAdmin = async () => {
  //     try {
  //       const currentUser = auth.currentUser;
  //       if (!currentUser) {
  //         setGateModalTitle('Coming Soon');
  //         setGateModalMessage('This feature is coming soon.');
  //         setGateModalVisible(true);
  //         setAccessBlocked(true);
  //         setUsers([]);
  //         return;
  //       }
  //       const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
  //       if (!userSnap.exists() || userSnap.data().userType !== 'admin') {
  //         setGateModalTitle('Coming Soon');
  //         setGateModalMessage('This feature is coming soon.');
  //         setGateModalVisible(true);
  //         setAccessBlocked(true);
  //         setUsers([]);
  //         return;
  //       }
  //     } catch (e) {
  //       console.error('Admin gate check failed:', e);
  //     }
  //   };
  //   checkAdmin();
  // }, []);

  const performSearch = async (searchTerm) => {
    if (accessBlocked) return;
    
    if (!searchTerm.trim()) {
      console.log('💬 CommunityList: Performing search, no search term');
      setDisplayedEntities(entities);
      setAllFilteredEntities([]);
      setIsEnhancedSearch(false);
      return;
    }

    // Wait for allEntities to be loaded
    if (allEntities.length === 0) {
      console.log('💬 CommunityList: Waiting for entities to load...');
      return;
    }

    try {
      setLoading(true);
      // Use context's filterEntitiesBySearch which handles all the logic
      const searchResults = await filterEntitiesBySearch(allEntities, searchTerm);
      
      setIsEnhancedSearch(searchResults.isEnhancedSearch);
      
      // Store all results and show first 25
      setAllFilteredEntities(searchResults.results);
      setDisplayedEntities(searchResults.results.slice(0, 25));
      console.log("Final Results:", searchResults.results);
    } catch (error) {
      console.error('Error searching entities:', error);
      setDisplayedEntities([]);
      setAllFilteredEntities([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    performSearch(searchQuery);
  };

  const handleUserPress = (entity) => {
    if (entity.entityType === 'organization') {
      navigation.navigate('OrganizationProfileScreen', { organizationId: entity.id });
    } else {
      navigation.navigate('UserProfileScreen', { userId: entity.id });
    }
  };

  const getAccountTypeColor = (accountType) => {
    const type = (accountType || '').toLowerCase();
    switch (type) {
      case 'individual':
        return '#2196F3'; // Blue
      case 'business':
        return '#4CAF50'; // Green
      case 'organization':
        return '#FFEB3B'; // Yellow
      case 'band':
        return '#F44336'; // Red
      case 'member':
        return '#2196F3'; // Treat member as blue
      default:
        return '#2196F3'; // Default to blue when unset/unknown
    }
  };

  const handleSendMessage = (entity) => {
    // Check if user is logged in
    if (!currentUserData) {
      // User is not logged in, show auth form
      setShowAuthModal(true);
      return;
    }
    
    // Check if current user is a community member
    // User is logged in but not a community member, show message
    if (!currentUserData.isCommunityMember) {
      setShowAuthModal(true);
      return;
    }
    
    // User is logged in and is a community member, proceed
    openMessaging(entity.id, entity.username);
  };

  const renderUserCard = (entity) => {
    const isOrganization = entity.entityType === 'organization';
    const truncatedBio = entity.bio && entity.bio.length > 120
      ? entity.bio.substring(0, 120) + '...'
      : entity.bio;

    // Define user type colors similar to Profile.js
    const USER_TYPES = {
      'ARTIST': { color: '#ff6b35', label: 'Artist' },
      'VENUE_OWNER': { color: '#4caf50', label: 'Venue Owner' },
      'PROMOTER': { color: '#9c27b0', label: 'Promoter' },
      'BOOKING_AGENT': { color: '#ff9800', label: 'Booking Agent' },
      'TOUR_MANAGER': { color: '#2196f3', label: 'Tour Manager' },
      'SOUND_ENGINEER': { color: '#607d8b', label: 'Sound Engineer' },
      'LIGHTING_TECHNICIAN': { color: '#ffeb3b', label: 'Lighting Technician' },
      'VENUE_EMPLOYEE': { color: '#795548', label: 'Venue Employee' },
      'FAN': { color: '#e91e63', label: 'Fan' },
      'MEMBER': { color: '#0bb5df', label: 'Member' }
    };

    // Get media - organizations use 'media' array, users use 'publicMedia.media'
    const mediaItems = isOrganization 
      ? (entity.media || [])
      : (entity.publicMedia?.media || []);

    if (loading || communityLoading) {
      return (
        <View style={globalStyles.loadingContainer}>
          <ActivityIndicator size="large" color="#fff" />
        </View>
      );
    }


    return (
      <TouchableOpacity
        key={entity.id || entity.username}
        style={styles.userCard}
        onPress={() => handleUserPress(entity)}
      >
        <View style={styles.profileCard}>
          {entity.photoURL ? (
            <Image source={{ uri: entity.photoURL }} style={styles.profileImage} />
          ) : (
            <View style={styles.profileImagePlaceholder}>
              <Text style={styles.profileImagePlaceholderText}>
                {isOrganization 
                  ? (entity.username ? entity.username[0].toUpperCase() : 'O')
                  : (entity.firstName ? entity.firstName[0].toUpperCase() :
                      entity.username ? entity.username[0].toUpperCase() : '?')}
              </Text>
            </View>
          )}

          {/* Paper Plane Button - Show for both users and organizations if current user is a community member */}
          <TouchableOpacity
            style={[
              styles.paperPlaneButton,
              {
                backgroundColor: getAccountTypeColor(isOrganization ? 'organization' : entity.accountType)
              }
            ]}
            onPress={(e) => {
              e.stopPropagation();
              handleSendMessage(entity);
            }}
          >
            <Image 
              source={require('../../assets/icons/paper-plane-icon.svg')} 
              style={[
                styles.paperPlaneIcon,
                { tintColor: '#FFFFFF' }
              ]} 
            />
          </TouchableOpacity>


          <Text style={styles.profileName}>
            {entity.username || (isOrganization ? 'Organization' : 'Anonymous User')}
          </Text>

          {entity.location && (
            <Text style={styles.locationText}>{entity.location}</Text>
          )}

          {entity.title && (
            <Text style={styles.titleText}>{entity.title}</Text>
          )}

          {/* Public Media Display */}
          {mediaItems && mediaItems.length > 0 && (
            <View style={styles.publicMediaContainer}>
              <View style={styles.mediaPreviewGrid}>
                {mediaItems.slice(0, 3).map((item, index) => (
                  <TouchableOpacity
                    key={item.id || index}
                    style={styles.mediaPreviewItem}
                    onPress={() => handleMediaPress(entity, index)}
                  >
                    {getFileType(item.name) === 'image' ? (
                      <Image
                        source={{ uri: item.url }}
                        style={styles.mediaPreviewImage}
                      />
                    ) : getFileType(item.name) === 'pdf' ? (
                      <View style={[styles.mediaPreviewIcon, { backgroundColor: '#e74c3c' }]}>
                        <Ionicons name="document-text" size={24} color="#ffffff" />
                        <Text
                          style={styles.mediaPreviewLabel}
                          numberOfLines={1}
                          ellipsizeMode="middle"
                        >
                          {item.name}
                        </Text>
                      </View>
                    ) : getFileType(item.name) === 'audio' ? (
                      <View style={styles.audioWaveformPreview}>
                        <View style={styles.audioWaveformContainer}>
                          <View style={[styles.audioWaveformBar, { height: 4 }]} />
                          <View style={[styles.audioWaveformBar, { height: 28 }]} />
                          <View style={[styles.audioWaveformBar, { height: 6 }]} />
                          <View style={[styles.audioWaveformBar, { height: 32 }]} />
                          <View style={[styles.audioWaveformBar, { height: 10 }]} />
                        </View>
                        {/* Web-only audio player for playback */}
                        {Platform.OS === 'web' && (
                          <audio src={item.url} controls style={{ width: '100%', marginTop: 6 }} />
                        )}
                      </View>
                    ) : getFileType(item.name) === 'video' ? (
                      <View style={styles.mediaPreviewContainer}>
                        <video
                          src={item.url}
                          style={styles.mediaPreviewImage}
                          muted
                          preload="metadata"
                        />
                        <View style={styles.mediaOverlay}>
                          <Ionicons name="play-circle" size={24} color="#ffffff" />
                        </View>
                        <View style={styles.videoDurationOverlay}>
                          <Text style={styles.durationText}>
                            {item.duration || '0:00'}
                          </Text>
                        </View>
                      </View>
                    ) : (
                      <View style={[styles.mediaPreviewIcon, { backgroundColor: '#34495e' }]}>
                        <Ionicons name="attach" size={16} color="#ffffff" />
                        <Text
                          style={styles.mediaPreviewLabel}
                          numberOfLines={1}
                          ellipsizeMode="middle"
                        >
                          FILE
                        </Text>
                      </View>
                    )}
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          )}

          {truncatedBio && (
            <Text style={styles.bioText}>{truncatedBio}</Text>
          )}

          {!isOrganization && entity.socialLinks && entity.socialLinks.length > 0 && (
            <View style={styles.socialLinksContainer}>
              {entity.socialLinks.map((link, index) => {
                const linkLower = (link || '').toLowerCase();
                let label = '🔗 Link';
                
                if (linkLower.includes('spotify')) label = '🎵 Spotify';
                else if (linkLower.includes('instagram')) label = '📷 Instagram';
                else if (linkLower.includes('facebook')) label = '👍 Facebook';
                else if (linkLower.includes('twitter') || linkLower.includes('x.com')) label = '🐦 Twitter';
                else if (linkLower.includes('youtube')) label = '▶️ YouTube';
                else if (linkLower.includes('soundcloud')) label = '🎧 SoundCloud';
                else if (linkLower.includes('tiktok')) label = '🎬 TikTok';
                else if (linkLower.includes('apple')) label = '🍎 Apple Music';
                else if (linkLower.includes('bandcamp')) label = '🎸 Bandcamp';
                
                return (
                  <TouchableOpacity 
                    key={index} 
                    style={styles.socialLinkButton}
                    onPress={() => Linking.openURL(link)}
                  >
                    <Text style={styles.socialLinkButtonText}>{label}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}

          <View style={styles.memberStats}>
            <Text style={styles.memberStat}>
              {isOrganization ? 'created' : 'member'} since: {new Date(entity.createdAt?.toDate?.() || entity.createdAt || Date.now()).toLocaleDateString(undefined, { year: 'numeric', month: 'short' })}
            </Text>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const handleScrollInternal = (event) => {
    const currentScrollY = event.nativeEvent.contentOffset.y;

    // Check if user has scrolled near the bottom to load more entities
    const { layoutMeasurement, contentOffset, contentSize } = event.nativeEvent;
    const isNearBottom = layoutMeasurement.height + contentOffset.y >= contentSize.height - 50;
    if (isNearBottom) {
      if (!searchQuery) {
        // For non-search case, use context's loadMoreEntities
        if (hasMore && !loadingMore) {
          loadMoreEntities();
        }
      } else {
        // For search results, load more from allFilteredEntities
        if (displayedEntities.length < allFilteredEntities.length) {
          console.log('📄 Loading more filtered entities...', `${displayedEntities.length}/${allFilteredEntities.length}`);
          const currentLength = displayedEntities.length;
          const nextBatch = allFilteredEntities.slice(currentLength, currentLength + 25);
          setDisplayedEntities(prev => [...prev, ...nextBatch]);
        }
      }
    }

    lastScrollY.current = currentScrollY;
  };
  
  useImperativeHandle(ref, () => ({
    handleScrollInternal
  }));
    // Expose handleScrollInternal to parent component
  


  return (
    <View style={[globalStyles.container, { paddingTop: 80, backgroundColor: '#1075ae' }]}>

      <View style={[globalStyles.overlay, { backgroundColor: 'transparent' }]}>
        <Text
          style={[
            homeStyles.homeSubtitle2,
            {
              color: '#dfffdd',
              opacity: 1,
            },
          ]}
        >
          Community Directory
        </Text>
        <View style={[venueListStyles.searchContainer, { borderBottomColor: 'transparent', borderOpacity: 0.5 }]}>
          <SearchBar
            color="#ffeca8"
            value={searchQuery}
            onChangeText={setSearchQuery}
            onSubmit={handleSearch}
            placeholder="Search community members..."
          />
        </View>


        <View
           style={{ flex: 1, width: '100%', flexGrow: 1 }} 
        >
          {accessBlocked ? (
            <View style={styles.loadingContainer}>
              {/* Gate modal will show */}
            </View>
          ) : loading || communityLoading ? (
            <View style={styles.loadingContainer}>
              <ActivityIndicator size="large" color="#3D7FE6" />
              <Text style={styles.loadingText}>Loading community members and organizations...</Text>
            </View>
          ) : displayedEntities.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>No results found</Text>
              <Text style={styles.emptySubtext}>
                {searchQuery ? 'Try a different search term' : 'Be the first to join the community!'}
              </Text>
            </View>
          ) : (
            <>

              <View style={[venueListStyles.venueCountContainer, { marginTop: 40 }]}>
                <Text style={venueListStyles.venueCountText}>
                  {searchQuery ? (
                    allFilteredEntities.length > 0 
                      ? `${displayedEntities.length} of ${allFilteredEntities.length} ${allFilteredEntities.length === 1 ? 'result' : 'results'} shown`
                      : `${displayedEntities.length} ${displayedEntities.length === 1 ? 'result' : 'results'} found`
                  ) : (
                    `${displayedEntities.length} ${displayedEntities.length === 1 ? 'result' : 'results'} found`
                  )}
                </Text>
                <TouchableOpacity
                  style={styles.seeLegendButton}
                  onPress={() => setLegendVisible(true)}
                >
                  <Text style={styles.seeLegendText}>See Legend</Text>
                </TouchableOpacity>
              </View>


              <View style={styles.communityGrid}>
                {displayedEntities.map(renderUserCard)}
              </View>

              {/* Show loading indicator when there are more items to load (matching VenueList pattern) */}
              {searchQuery && displayedEntities.length < allFilteredEntities.length && (
                <View style={styles.loadingMoreContainer}>
                  <ActivityIndicator size="small" color="#4e7de0" />
                  <Text style={styles.loadingMoreText}>Loading more...</Text>
                </View>
              )}


              {!searchQuery && !hasMore && displayedEntities.length > 0 && (
                <View style={styles.endContainer}>
                  <Text style={styles.endText}>You've reached the end!</Text>
                </View>
              )}

              {searchQuery && displayedEntities.length >= allFilteredEntities.length && allFilteredEntities.length > 0 && (
                <View style={styles.endContainer}>
                  <Text style={styles.endText}>You've reached the end!</Text>
                </View>
              )}
            </>
          )}
        </View>

        {/* MediaViewer Modal */}
        {isMediaViewerVisible && (
          <MediaViewer
            visible={isMediaViewerVisible}
            onClose={handleMediaViewerClose}
            media={currentUserMedia}
            currentIndex={selectedMediaIndex}
            onNavigate={setSelectedMediaIndex}
          />
        )}

        {/* Access Gate Popup */}
        <Modal
          visible={gateModalVisible}
          transparent={true}
          animationType="fade"
          onRequestClose={() => {
            setGateModalVisible(false);
            navigation.goBack();
          }}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.legendModal}>
              <View style={styles.legendHeader}>
                <Text style={styles.legendModalTitle}>{gateModalTitle}</Text>
                <TouchableOpacity
                  style={styles.closeButton}
                  onPress={() => {
                    setGateModalVisible(false);
                    navigation.goBack();
                  }}
                >
                  <Text style={styles.closeButtonText}>×</Text>
                </TouchableOpacity>
              </View>
              <View style={styles.legendModalContent}>
                <Text style={styles.legendModalText}>{gateModalMessage}</Text>
                <View style={{ marginTop: 16, alignItems: 'center' }}>
                  <TouchableOpacity
                    style={[styles.seeLegendButton, { backgroundColor: 'rgba(255, 255, 255, 0.25)' }]}
                    onPress={() => {
                      setGateModalVisible(false);
                      navigation.goBack();
                    }}
                  >
                    <Text style={styles.seeLegendText}>OK</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          </View>
        </Modal>

        {/* Legend Popup Modal */}
        <Modal
          visible={legendVisible}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setLegendVisible(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={styles.legendModal}>
              <View style={styles.legendHeader}>
                <Text style={styles.legendModalTitle}>Account Types</Text>
                <TouchableOpacity
                  style={styles.closeButton}
                  onPress={() => setLegendVisible(false)}
                >
                  <Text style={styles.closeButtonText}>×</Text>
                </TouchableOpacity>
              </View>
              
              <View style={styles.legendModalContent}>
                <View style={styles.legendModalItems}>
                  <View style={styles.legendModalItem}>
                    <View style={[styles.legendIconContainer, { backgroundColor: '#2196F3' }]}>
                      <Image 
                        source={require('../../assets/icons/paper-plane-icon.svg')} 
                        style={[styles.legendIcon, { tintColor: '#ffffff' }]} 
                      />
                    </View>
                    <Text style={styles.legendModalText}>Individual</Text>
                  </View>
                  
                  <View style={styles.legendModalItem}>
                    <View style={[styles.legendIconContainer, { backgroundColor: '#4CAF50' }]}>
                      <Image 
                        source={require('../../assets/icons/paper-plane-icon.svg')} 
                        style={[styles.legendIcon, { tintColor: '#ffffff' }]} 
                      />
                    </View>
                    <Text style={styles.legendModalText}>Business</Text>
                  </View>
                  
                  <View style={styles.legendModalItem}>
                    <View style={[styles.legendIconContainer, { backgroundColor: '#FFEB3B' }]}>
                      <Image 
                        source={require('../../assets/icons/paper-plane-icon.svg')} 
                        style={[styles.legendIcon, { tintColor: '#ffffff' }]} 
                      />
                    </View>
                    <Text style={styles.legendModalText}>Organization</Text>
                  </View>
                  
                  <View style={styles.legendModalItem}>
                    <View style={[styles.legendIconContainer, { backgroundColor: '#F44336' }]}>
                      <Image 
                        source={require('../../assets/icons/paper-plane-icon.svg')} 
                        style={[styles.legendIcon, { tintColor: '#ffffff' }]} 
                      />
                    </View>
                    <Text style={styles.legendModalText}>Band</Text>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </Modal>

        {/* Auth/Community Member Modal */}
          {!currentUserData ? (
            // User is not logged in, show auth form
            <Modal visible={showAuthModal && !currentUserData} transparent={true} animationType="fade" onRequestClose={() => setShowAuthModal(false)}>
              <View style={styles.authModalOverlay}>
                <CommunityAuthForm isModal={true} onClose={() => setShowAuthModal(false)} />
              </View>
            </Modal>
          ) : (
            // User is logged in but not a community member, show upgrade form
            <Modal visible={showAuthModal && currentUserData && !currentUserData.isCommunityMember} transparent={true} animationType="fade" onRequestClose={() => setShowAuthModal(false)}>
              <View style={styles.authModalOverlay}>
                <CommunityMemberUpgradeForm
                  isModal={true}
                  onClose={() => setShowAuthModal(false)}
                  message="You must be a community member to send messages."
                  onSuccess={() => {
                    setShowAuthModal(false);
                    setShowCommunityMemberSuccessModal(true);
                  }}
                  onError={(error) => {
                    console.error('Error upgrading to community member:', error);
                  }}
                />
              </View>
            </Modal>
          )}

        {/* Community Member Success Modal */}
        <Modal
          visible={showCommunityMemberSuccessModal}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setShowCommunityMemberSuccessModal(false)}
        >
          <View style={styles.modalOverlay}>
            <View style={{
              backgroundColor: '#ffffff',
              borderRadius: 12,
              padding: 20,
              width: '100%',
              maxWidth: 400,
              alignItems: 'center',
            }}>
              <Ionicons name="checkmark-circle" size={48} color="#2ecc71" />
              <Text style={{
                fontSize: 18,
                fontFamily: 'Glacial Indifference',
                marginTop: 10,
                marginBottom: 15,
                textAlign: 'center',
                color: '#2ecc71',
              }}>Congratulations!</Text>

              <Text style={{
                fontSize: 14,
                color: '#333',
                textAlign: 'center',
                marginBottom: 20,
                lineHeight: 20,
              }}>You are now a community member.</Text>

              <TouchableOpacity
                onPress={() => setShowCommunityMemberSuccessModal(false)}
                style={{
                  backgroundColor: '#2ecc71',
                  borderRadius: 8,
                  paddingHorizontal: 30,
                  paddingVertical: 12,
                  alignItems: 'center',
                }}
              >
                <Text style={{
                  color: '#ffffff',
                  fontSize: 16,
                  fontFamily: 'Glacial Indifference',
                  fontWeight: 'bold',
                }}>OK</Text>
              </TouchableOpacity>
            </View>
          </View>
        </Modal>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 0.4,
    },
    '50%': {
      opacity: 1,
    },
  },
  communityGrid: {
    flex: 1,
    width: '100%',
    maxWidth: 1400,
    flexGrow: 1,
    alignSelf: 'center',
    marginTop: 35,
    display: Platform.OS === 'web' ? 'grid' : 'flex',
    ...(Platform.OS === 'web' && {
      gridTemplateColumns: getResponsiveValue({
        mobile: 'repeat(1, 1fr)',
        tablet: 'repeat(2, 1fr)',
        laptop: 'repeat(3, 1fr)',
        desktop: 'repeat(3, 1fr)'
      }),
      gap: getResponsiveValue({
        mobile: 15,
        tablet: 15,
        laptop: 25,
        desktop: 30
      }),
      alignItems: 'center',
      justifyItems: getResponsiveValue({
        mobile: 'center',
        tablet: 'stretch',
        laptop: 'stretch',
        desktop: 'stretch'
      }),
      paddingHorizontal: getResponsiveValue({
        mobile: 0,
        tablet: 20,
        laptop: 20,
        desktop: 20
      }),
      marginBottom: getResponsiveValue({
        mobile: 20,
        tablet: 30,
        laptop: 40,
        desktop: 40
      }),
    }),
    // Mobile flexbox fallback
    ...(Platform.OS !== 'web' && {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingBottom: 20,
    }),
  },
  userCard: {
    backgroundColor: '#feffe4',
    borderRadius: 12,
    height: getResponsiveValue({
      mobile: 380,
      tablet: 440,
      laptop: 480,
      desktop: 500
    }),
    position: 'relative',
    overflow: 'hidden',
    width: getResponsiveValue({
      mobile: '90vw',
      tablet: '100%',
      laptop: '100%',
      desktop: '100%'
    }),
    marginLeft: getResponsiveValue({
      mobile: 'auto',
      tablet: 0,
      laptop: 0,
      desktop: 0
    }),
    marginRight: getResponsiveValue({
      mobile: 'auto',
      tablet: 0,
      laptop: 0,
      desktop: 0
    }),
    marginBottom: Platform.OS !== 'web' ? 16 : 0,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
      ':hover': {
        transform: 'scale(1.02)',
        boxShadow: '0 12px 20px rgba(0,0,0,0.3)',
      },
    }),
  },
  profileCard: {
    height: '100%',
    width: '100%',
    paddingTop: getResponsiveValue({
      mobile: 15,
      tablet: 20,
      laptop: 25,
      desktop: 30
    }),
    paddingHorizontal: getResponsiveValue({
      mobile: 15,
      tablet: 20,
      laptop: 25,
      desktop: 30
    }),
    paddingBottom: getResponsiveValue({
      mobile: 35,
      tablet: 40,
      laptop: 45,
      desktop: 50
    }),
    gap: getResponsiveValue({
      mobile: 4,
      tablet: 6,
      laptop: 6,
      desktop: 8
    }),
    alignItems: 'center',
    position: 'relative',
  },
  profileImage: {
    width: getResponsiveValue({
      mobile: 60,
      tablet: 70,
      laptop: 80,
      desktop: 90
    }),
    height: getResponsiveValue({
      mobile: 60,
      tablet: 70,
      laptop: 80,
      desktop: 90
    }),
    borderRadius: getResponsiveValue({
      mobile: 30,
      tablet: 35,
      laptop: 40,
      desktop: 45
    }),
    marginBottom: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
  },
  profileImagePlaceholder: {
    width: getResponsiveValue({
      mobile: 60,
      tablet: 70,
      laptop: 80,
      desktop: 90
    }),
    height: getResponsiveValue({
      mobile: 60,
      tablet: 70,
      laptop: 80,
      desktop: 90
    }),
    borderRadius: getResponsiveValue({
      mobile: 30,
      tablet: 35,
      laptop: 40,
      desktop: 45
    }),
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
  },
  profileImagePlaceholderText: {
    color: '#000000',
    fontSize: getResponsiveValue({
      mobile: 24,
      tablet: 28,
      laptop: 32,
      desktop: 36
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  paperPlaneButton: {
    position: 'absolute',
    top: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
    right: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
    borderRadius: getResponsiveValue({
      mobile: 10,
      tablet: 12,
      laptop: 14,
      desktop: 16
    }),
    width: getResponsiveValue({
      mobile: 32,
      tablet: 36,
      laptop: 40,
      desktop: 40
    }),
    height: getResponsiveValue({
      mobile: 32,
      tablet: 36,
      laptop: 40,
      desktop: 40
    }),
    padding: getResponsiveValue({
      mobile: 2,
      tablet: 2,
      laptop: 3,
      desktop: 3
    }),
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'scale(1.1)',
        opacity: 0.9,
      }
    }),
  },
  paperPlaneIcon: {
    width: '80%',
    height: '70%',
  },
  legendContainer: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    padding: getResponsiveValue({
      mobile: 12,
      tablet: 16,
      laptop: 20,
      desktop: 20
    }),
    marginHorizontal: getResponsiveValue({
      mobile: 20,
      tablet: 40,
      laptop: 60,
      desktop: 80
    }),
    marginVertical: getResponsiveValue({
      mobile: 15,
      tablet: 20,
      laptop: 25,
      desktop: 30
    }),
    alignItems: 'center',
  },
  legendTitle: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 14,
      tablet: 16,
      laptop: 18,
      desktop: 18
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    marginBottom: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
  },
  legendItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: getResponsiveValue({
      mobile: 12,
      tablet: 16,
      laptop: 20,
      desktop: 24
    }),
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: getResponsiveValue({
      mobile: 6,
      tablet: 8,
      laptop: 10,
      desktop: 10
    }),
  },
  legendIconContainer: {
    width: getResponsiveValue({
      mobile: 20,
      tablet: 24,
      laptop: 28,
      desktop: 28
    }),
    height: getResponsiveValue({
      mobile: 20,
      tablet: 24,
      laptop: 28,
      desktop: 28
    }),
    borderRadius: getResponsiveValue({
      mobile: 10,
      tablet: 12,
      laptop: 14,
      desktop: 14
    }),
    padding: getResponsiveValue({
      mobile: 4,
      tablet: 5,
      laptop: 6,
      desktop: 6
    }),
    alignItems: 'center',
    justifyContent: 'center',
  },
  legendIcon: {
    width: '100%',
    height: '100%',
  },
  legendText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 13,
      laptop: 14,
      desktop: 14
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
  },
  seeLegendButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: getResponsiveValue({
      mobile: 10,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    paddingVertical: getResponsiveValue({
      mobile: 3,
      tablet: 4,
      laptop: 5,
      desktop: 5
    }),
    borderRadius: getResponsiveValue({
      mobile: 15,
      tablet: 18,
      laptop: 20,
      desktop: 20
    }),
    marginTop: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    alignSelf: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        transform: 'scale(1.05)',
      }
    }),
  },
  seeLegendText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: getResponsiveValue({
      mobile: 20,
      tablet: 40,
      laptop: 60,
      desktop: 80
    }),
  },
  legendModal: {
    backgroundColor: '#3D7FE6',
    borderRadius: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
    width: '100%',
    maxWidth: getResponsiveValue({
      mobile: 320,
      tablet: 400,
      laptop: 480,
      desktop: 520
    }),
    maxHeight: '80%',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
    }),
  },
  legendHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.2)',
  },
  legendModalTitle: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 18,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
  },
  closeButton: {
    width: getResponsiveValue({
      mobile: 32,
      tablet: 36,
      laptop: 40,
      desktop: 40
    }),
    height: getResponsiveValue({
      mobile: 32,
      tablet: 36,
      laptop: 40,
      desktop: 40
    }),
    borderRadius: getResponsiveValue({
      mobile: 16,
      tablet: 18,
      laptop: 20,
      desktop: 20
    }),
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        transform: 'scale(1.1)',
      }
    }),
  },
  closeButtonText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 20,
      tablet: 22,
      laptop: 24,
      desktop: 24
    }),
    fontWeight: 'bold',
  },
  legendModalContent: {
    padding: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
  },
  legendModalItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    gap: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
  },
  legendModalItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    minWidth: getResponsiveValue({
      mobile: 120,
      tablet: 140,
      laptop: 160,
      desktop: 160
    }),
  },
  legendModalText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 14,
      tablet: 16,
      laptop: 18,
      desktop: 18
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  legendIcon: {
    width: '100%',
    height: '100%',
    resizeMode: 'contain',
  },
  legendText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 13,
      laptop: 14,
      desktop: 14
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
  },
  seeLegendButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    paddingHorizontal: getResponsiveValue({
      mobile: 10,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    paddingVertical: getResponsiveValue({
      mobile: 3,
      tablet: 4,
      laptop: 5,
      desktop: 5
    }),
    borderRadius: getResponsiveValue({
      mobile: 15,
      tablet: 18,
      laptop: 20,
      desktop: 20
    }),
    marginTop: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    alignSelf: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        transform: 'scale(1.05)',
      }
    }),
  },
  seeLegendText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: getResponsiveValue({
      mobile: 20,
      tablet: 40,
      laptop: 60,
      desktop: 80
    }),
  },
  legendModal: {
    backgroundColor: '#3D7FE6',
    borderRadius: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
    width: '100%',
    maxWidth: getResponsiveValue({
      mobile: 320,
      tablet: 400,
      laptop: 480,
      desktop: 520
    }),
    maxHeight: '80%',
    ...(Platform.OS === 'web' && {
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
    }),
  },
  legendHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.2)',
  },
  legendModalTitle: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 18,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '700',
  },
  closeButton: {
    width: getResponsiveValue({
      mobile: 32,
      tablet: 36,
      laptop: 40,
      desktop: 40
    }),
    height: getResponsiveValue({
      mobile: 32,
      tablet: 36,
      laptop: 40,
      desktop: 40
    }),
    borderRadius: getResponsiveValue({
      mobile: 16,
      tablet: 18,
      laptop: 20,
      desktop: 20
    }),
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
        transform: 'scale(1.1)',
      }
    }),
  },
  closeButtonText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 20,
      tablet: 22,
      laptop: 24,
      desktop: 24
    }),
    fontWeight: 'bold',
  },
  legendModalContent: {
    padding: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
  },
  legendModalItems: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
    gap: getResponsiveValue({
      mobile: 16,
      tablet: 20,
      laptop: 24,
      desktop: 24
    }),
  },
  legendModalItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 12
    }),
    minWidth: getResponsiveValue({
      mobile: 120,
      tablet: 140,
      laptop: 160,
      desktop: 160
    }),
  },
  legendModalText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 14,
      tablet: 16,
      laptop: 18,
      desktop: 18
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  verificationText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 14,
      laptop: 16,
      desktop: 18
    }),
    fontWeight: 'bold',
  },
  profileName: {
    color: '#000000',
    fontSize: getResponsiveValue({
      mobile: 16,
      tablet: 17,
      laptop: 18,
      desktop: 20
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: getResponsiveValue({
      mobile: 2,
      tablet: 3,
      laptop: 4,
      desktop: 5
    }),
  },
  userTypeText: {
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 13,
      laptop: 14,
      desktop: 15
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: getResponsiveValue({
      mobile: 2,
      tablet: 3,
      laptop: 4,
      desktop: 5
    }),
  },
  verificationStatus: {
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 11,
      laptop: 12,
      desktop: 13
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: getResponsiveValue({
      mobile: 4,
      tablet: 6,
      laptop: 8,
      desktop: 10
    }),
  },
  locationText: {
    color: 'rgba(0, 0, 0, 0.7)',
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 11,
      laptop: 12,
      desktop: 13
    }),
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
  },
  titleText: {
    color: '#578dff',
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 11,
      laptop: 12,
      desktop: 13
    }),
    fontFamily: 'Glacial Indifference',
    fontWeight: 'bold',
  },
  bioText: {
    color: 'rgba(0, 0, 0, 0.8)',
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 11,
      laptop: 12,
      desktop: 13
    }),
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
    lineHeight: getResponsiveValue({
      mobile: 14,
      tablet: 15,
      laptop: 16,
      desktop: 18
    }),
    paddingHorizontal: getResponsiveValue({
      mobile: 6,
      tablet: 8,
      laptop: 10,
      desktop: 12
    }),
  },
  socialLinksContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 14
    }),
    marginBottom: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 14
    }),
    marginHorizontal: getResponsiveValue({
      mobile: -3,
      tablet: -4,
      laptop: -5,
      desktop: -6
    }),
  },
  socialLinkButton: {
    backgroundColor: '#3b82f6',
    paddingHorizontal: getResponsiveValue({
      mobile: 10,
      tablet: 12,
      laptop: 14,
      desktop: 16
    }),
    paddingVertical: getResponsiveValue({
      mobile: 5,
      tablet: 6,
      laptop: 7,
      desktop: 8
    }),
    borderRadius: 16,
    marginHorizontal: getResponsiveValue({
      mobile: 3,
      tablet: 4,
      laptop: 5,
      desktop: 6
    }),
    marginVertical: getResponsiveValue({
      mobile: 3,
      tablet: 4,
      laptop: 5,
      desktop: 6
    }),
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  socialLinkButtonText: {
    color: '#ffffff',
    fontSize: getResponsiveValue({
      mobile: 10,
      tablet: 11,
      laptop: 12,
      desktop: 13
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  memberStats: {
    position: 'absolute',
    bottom: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
    width: '100%',
    alignItems: 'center',
  },
  memberStat: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: getResponsiveValue({
      mobile: 9,
      tablet: 10,
      laptop: 11,
      desktop: 12
    }),
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
  },

  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    minHeight: 200,
  },
  loadingText: {
    color: 'rgba(0, 0, 0, 0.8)',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    marginTop: 15,
    textAlign: 'center',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    minHeight: 200,
  },
  emptyText: {
    color: 'rgba(0, 0, 0, 0.8)',
    fontSize: 18,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    marginBottom: 10,
    textAlign: 'center',
  },
  emptySubtext: {
    color: 'rgba(0, 0, 0, 0.8)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    textAlign: 'center',
  },
  resultsHeader: {
    padding: 16,
  },
  resultsCount: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  enhancedSearchBadge: {
    backgroundColor: 'rgba(78, 125, 224, 0.2)',
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 4,
    marginTop: 8,
    alignSelf: 'flex-start',
  },
  enhancedSearchText: {
    color: '#4e7de0',
    fontSize: 12,
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
  },
  loadingMoreContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  loadingMoreText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
    marginLeft: 10,
  },
  endContainer: {
    alignItems: 'center',
    padding: 20,
  },
  endText: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 14,
    fontFamily: 'VastagoGrotesk',
  },
  publicMediaContainer: {
    width: '100%',
    marginBottom: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
  },
  publicMediaTitle: {
    color: '#000000',
    fontSize: getResponsiveValue({
      mobile: 11,
      tablet: 12,
      laptop: 13,
      desktop: 14
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: '600',
    marginBottom: getResponsiveValue({
      mobile: 4,
      tablet: 5,
      laptop: 6,
      desktop: 8
    }),
  },
  mediaItemsContainer: {
    gap: getResponsiveValue({
      mobile: 2,
      tablet: 3,
      laptop: 4,
      desktop: 5
    }),
  },
  mediaPreviewGrid: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: getResponsiveValue({
      mobile: 4,
      tablet: 6,
      laptop: 8,
      desktop: 10
    }),
  },
  mediaPreviewItem: {
    width: '30%',
    aspectRatio: 1,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    overflow: 'hidden',
    position: 'relative',
  },
  mediaPreviewContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  mediaOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoDurationOverlay: {
    position: 'absolute',
    bottom: 4,
    right: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 4,
    paddingVertical: 2,
    borderRadius: 4,
  },
  durationText: {
    color: '#ffffff',
    fontSize: 8,
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
  mediaPreviewImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  mediaPreviewIcon: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    flexDirection: 'column',
  },
  mediaPreviewLabel: {
    color: '#ffffff',
    fontSize: 8,
    fontFamily: 'VastagoGrotesk',
    marginTop: 2,
    fontWeight: 'bold',
    textAlign: 'center',
    numberOfLines: 1,
    ellipsizeMode: 'middle',
  },
  audioWaveformPreview: {
    width: '100%',
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: 4,
    padding: 8,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  audioWaveformContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    height: 40,
    width: '100%',
  },
  audioWaveformBar: {
    width: 2,
    backgroundColor: '#aaa',
    marginHorizontal: 1,
    borderRadius: 1,
    minHeight: 8,
  },
  audioPlayIcon: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 12,
    width: 24,
    height: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mediaItem: {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 4,
    padding: getResponsiveValue({
      mobile: 4,
      tablet: 5,
      laptop: 6,
      desktop: 8
    }),
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  mediaItemName: {
    color: '#000000',
    fontSize: getResponsiveValue({
      mobile: 9,
      tablet: 10,
      laptop: 11,
      desktop: 12
    }),
    fontFamily: 'VastagoGrotesk',
  },
  moreMediaText: {
    color: 'rgba(0, 0, 0, 0.6)',
    fontSize: getResponsiveValue({
      mobile: 8,
      tablet: 9,
      laptop: 10,
      desktop: 11
    }),
    fontFamily: 'VastagoGrotesk',
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: getResponsiveValue({
      mobile: 2,
      tablet: 3,
      laptop: 4,
      desktop: 5
    }),
  },
  organizationBadge: {
    position: 'absolute',
    top: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
    left: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 15
    }),
    paddingHorizontal: getResponsiveValue({
      mobile: 6,
      tablet: 8,
      laptop: 10,
      desktop: 12
    }),
    paddingVertical: getResponsiveValue({
      mobile: 2,
      tablet: 3,
      laptop: 4,
      desktop: 5
    }),
    borderRadius: getResponsiveValue({
      mobile: 8,
      tablet: 10,
      laptop: 12,
      desktop: 14
    }),
    zIndex: 999,
  },
  authModalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
  authModalContent: {
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  authModalTitle: {
    fontSize: getResponsiveValue({
      mobile: 12,
      tablet: 18,
      laptop: 20,
      desktop: 22
    }),
    fontFamily: 'Glacial Indifference',
    fontWeight: 'bold',
    color: '#424b54',
    marginBottom: 20,
    textAlign: 'center',
  },
  authModalMessage: {
    fontSize: getResponsiveValue({
      mobile: 16,
      tablet: 17,
      laptop: 18,
      desktop: 20
    }),
    marginBottom: 10,
    fontFamily: 'Glacial Indifference',
    color: '#424b54',
    textAlign: 'center',
    lineHeight: 24,
  },
  organizationBadgeText: {
    color: '#000000',
    fontSize: getResponsiveValue({
      mobile: 9,
      tablet: 10,
      laptop: 11,
      desktop: 12
    }),
    fontFamily: 'VastagoGrotesk',
    fontWeight: 'bold',
  },
});

export default CommunityList;
